// =============================================================================
// useProfiles — Centralized profiles cache
// =============================================================================
// PURPOSE: Eliminate redundant .from("profiles") sequential scans across hooks
// and components. Module-level cache with 5-minute TTL means multiple hook
// instances share one fetch per org instead of firing N independent queries.
//
// USAGE:
//   const { getProfileById, getProfileByGhlId, allProfiles, isLoading } = useProfiles(orgId);
//
// REQUIREMENTS:
// - organization_id scoped — only fetches profiles for org members
// - 5-minute TTL (profiles change rarely)
// - Multiple callers in the same session share one cached result
// - Stale-while-revalidate: returns cached data immediately, refreshes in background
//
// KEY DECISIONS:
// - Module-level Map (not React state) for cross-instance cache sharing
// - TTL eviction prevents serving profiles from dissolved orgs across sessions
// - Scoped to organization_members to avoid fetching all global profiles
// - Does NOT use AuthContext or context/provider — standalone hook, lower blast radius
//
// MIGRATION: Existing .from("profiles") call sites are NOT migrated here.
//   TODO markers are left at each call site for incremental migration.
//   See: Phase 7.2 in docs/PLAN_rbac_security_scale_hardening.md
// =============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";

const PROFILES_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  ghl_user_id: string | null;
  avatar_url: string | null;
  /** organization_members.role for THIS org (exec/client/closer/setter/dialer/manager). */
  role: string | null;
}

interface CacheEntry {
  profiles: Profile[];
  fetchedAt: number;
}

// Module-level cache: shared across all hook instances in the same browser session.
// Key: organizationId
const profilesCache = new Map<string, CacheEntry>();
// Stable empty list: a fresh [] per render would churn getProfileById's identity.
const EMPTY_PROFILES: Profile[] = [];

function isCacheValid(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < PROFILES_TTL_MS;
}

interface UseProfilesResult {
  allProfiles: Profile[];
  isLoading: boolean;
  error: string | null;
  /** Look up a profile by Supabase user ID */
  getProfileById: (id: string) => Profile | undefined;
  /** Look up a profile by GHL user ID (TEXT, not UUID) */
  getProfileByGhlId: (ghlId: string) => Profile | undefined;
}

// In-flight dedup: every mounted useProfiles for one org (a modal with 20 subtask rows
// mounts 40+) shares ONE roster request instead of each firing its own pair of queries.
const inFlight = new Map<string, Promise<Profile[]>>();
// A cache entry younger than this is served without any refresh. Without it, rows that
// mount just after a fetch settles (expanding a subtask, opening a picker) each refetch.
const FRESH_MS = 30 * 1000;

// The roster is viewer-dependent (the staff union is gated on the viewer being a
// super-admin), but both maps are keyed by org only. Drop them whenever the signed-in
// user changes so the next viewer never inherits the previous viewer's roster.
// rosterGen bumps on every viewer change. A fetch that started under an older
// generation neither writes the cache nor hands its roster to a caller.
let rosterViewerId: string | null | undefined;
let rosterGen = 0;
// Mounted hook instances to notify on a viewer change, so one that stays mounted across
// a login/logout drops the previous viewer's roster and refetches.
const rosterListeners = new Set<() => void>();
if (typeof supabase?.auth?.onAuthStateChange === "function") {
  supabase.auth.onAuthStateChange((_event, session) => {
    const viewerId = session?.user?.id ?? null;
    if (viewerId === rosterViewerId) return; // token refresh etc.: same viewer
    rosterViewerId = viewerId;
    rosterGen++;
    profilesCache.clear();
    inFlight.clear();
    rosterListeners.forEach((notify) => notify());
  });
}

function sharedRosterFetch(organizationId: string): Promise<Profile[]> {
  const pending = inFlight.get(organizationId);
  if (pending) return pending;
  const run: Promise<Profile[]> = fetchRosterUncached(organizationId, rosterGen).finally(() => {
    // Only remove our own entry: after a viewer change a newer fetch may own this key.
    if (inFlight.get(organizationId) === run) inFlight.delete(organizationId);
  });
  inFlight.set(organizationId, run);
  return run;
}

async function loadRoster(organizationId: string): Promise<Profile[]> {
  const gen = rosterGen;
  const flat = await sharedRosterFetch(organizationId);
  // The viewer changed mid-fetch: that roster was built for the previous viewer.
  if (gen !== rosterGen) return loadRoster(organizationId);
  return flat;
}

async function fetchRosterUncached(organizationId: string, gen: number): Promise<Profile[]> {
  if (!supabase) return [];
  // 1) Org members (active) — the per-org roster (role lives on the member row).
  const { data, error: fetchError } = await supabase
    .from("organization_members")
    .select("role, profiles!inner(id, full_name, email, ghl_user_id, avatar_url)")
    .eq("organization_id", organizationId)
    .eq("status", "active");

  if (fetchError) throw fetchError;

  const memberProfiles: Profile[] = (data || [])
    .map((row) => {
      // role is on the member row; profiles is the joined account.
      const r = row as unknown as { role: string | null; profiles: Profile | null };
      return r.profiles ? { ...r.profiles, role: r.role ?? null } : null;
    })
    .filter((p): p is Profile => p != null);

  // 2) Dynamis staff: super-admins span every org (mirrors the get_user_orgs
  //    god-mode branch) so a Dynamis exec can assign/@mention a co-super-admin
  //    (e.g. Mako) on a client task even though Mako has no membership row.
  //    GATE: `profiles` SELECT is world-readable to every authenticated user
  //    (two `USING (true)` policies, live), so an UNGATED union would leak the
  //    Dynamis staff roster into a CLIENT's pickers. Only union the staff roster
  //    when the VIEWER is themselves a super-admin. Stamp role 'exec' so a
  //    super-admin with no membership row still passes isPortalAssignableRole in
  //    the assignee pickers. Best-effort — never regresses the org roster.
  let adminProfiles: Profile[] = [];
  try {
    // Session lookup and the super-admin roster query are independent —
    // the query filters on is_super_admin=true only, viewerId is used purely
    // to gate exposure afterward — so fire them concurrently (one wave, not
    // two) instead of awaiting the session before starting the query.
    const [sessionResult, adminResult] = await Promise.all([
      supabase.auth.getSession(),
      supabase
        .from("profiles")
        .select("id, full_name, email, ghl_user_id, avatar_url, is_super_admin")
        .eq("is_super_admin", true),
    ]);
    const viewerId = sessionResult.data.session?.user?.id ?? null;
    const { data: adminData, error: adminError } = adminResult;
    if (adminError) throw adminError;
    const admins = (adminData ?? []) as unknown as Array<Profile & { is_super_admin: boolean }>;
    // Viewer-gate: only surface the staff roster to a super-admin viewer.
    if (viewerId && admins.some((a) => a.id === viewerId)) {
      adminProfiles = admins.map((a) => ({
        id: a.id,
        full_name: a.full_name,
        email: a.email,
        ghl_user_id: a.ghl_user_id,
        avatar_url: a.avatar_url,
        role: "exec",
      }));
    }
  } catch (adminErr) {
    logger.warn("useProfiles: super-admin union fetch failed; using org members only", adminErr);
  }

  // 3) Merge + dedup by id. Members iterated first so their real org role wins on
  //    tie; a super-admin who is also a member (Nathaniel) collapses to one, and
  //    duplicate organization_members rows for one user collapse too.
  const byId = new Map<string, Profile>();
  for (const p of [...memberProfiles, ...adminProfiles]) {
    if (p?.id && !byId.has(p.id)) byId.set(p.id, p);
  }
  const flat: Profile[] = Array.from(byId.values());

  // Cache is keyed by org, so it is correct even if the active org moved on.
  if (gen === rosterGen) {
    profilesCache.set(organizationId, { profiles: flat, fetchedAt: Date.now() });
  }
  return flat;
}

export function useProfiles(organizationId: string | null): UseProfilesResult {
  const cached = organizationId ? profilesCache.get(organizationId) : null;

  // Profiles are stored WITH the org they belong to. On an org switch the old org's
  // members must never render in the new org's pickers/@mentions (cross-tenant roster
  // leak), so the returned list is derived: a mismatching org falls back to that
  // org's own cache entry, or empty, instead of whatever was fetched last.
  const [loaded, setLoaded] = useState<{ orgId: string | null; list: Profile[] }>({
    orgId: organizationId,
    list: cached?.profiles ?? EMPTY_PROFILES,
  });
  // Org whose foreground fetch has settled (success OR error). isLoading is derived
  // from it so it is correct on the very first render after an org change, and is
  // false when there is no org at all (a null org used to leave consumers stuck on a
  // spinner forever: the initial `true` was never cleared).
  const [settledOrgId, setSettledOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Latest-request-wins: a slow response for org A must not land after org B is active.
  const reqIdRef = useRef(0);
  // Bumps when the signed-in viewer changes (see rosterListeners): re-runs the fetch effect.
  const [viewerGen, setViewerGen] = useState(rosterGen);
  useEffect(() => {
    const onViewerChange = () => {
      setLoaded({ orgId: null, list: EMPTY_PROFILES });
      setSettledOrgId(null);
      setViewerGen(rosterGen);
    };
    rosterListeners.add(onViewerChange);
    return () => {
      rosterListeners.delete(onViewerChange);
    };
  }, []);

  const profiles = organizationId
    ? loaded.orgId === organizationId
      ? loaded.list
      : (cached?.profiles ?? EMPTY_PROFILES)
    : EMPTY_PROFILES;
  const isLoading =
    !!organizationId && settledOrgId !== organizationId && !(cached && isCacheValid(cached));

  // Via a callback (not `.current` in the effect cleanup) to keep exhaustive-deps quiet.
  const invalidateRequests = useCallback(() => {
    reqIdRef.current++;
  }, []);

  const fetchProfiles = useCallback(async () => {
    if (!organizationId) return;
    if (!supabase) {
      setSettledOrgId(organizationId); // nothing to fetch: settle instead of spinning forever
      return;
    }

    const reqId = ++reqIdRef.current;
    const isStale = () => reqId !== reqIdRef.current;

    setError(null);

    const entry = profilesCache.get(organizationId);
    if (entry && Date.now() - entry.fetchedAt < FRESH_MS) {
      setLoaded({ orgId: organizationId, list: entry.profiles });
      setSettledOrgId(organizationId);
      return;
    }

    try {
      const flat = await loadRoster(organizationId);
      if (isStale()) return;
      setLoaded({ orgId: organizationId, list: flat });
    } catch (err) {
      if (isStale()) return;
      logger.error("useProfiles: Failed to fetch profiles", err);
      setError(err instanceof Error ? err.message : "Failed to fetch profiles");
    } finally {
      if (!isStale()) setSettledOrgId(organizationId);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    // Cache hit is served straight from render (see `profiles` / `isLoading` above);
    // either way one fetch refreshes it (background when valid, foreground otherwise).
    void fetchProfiles();
    // Org changed or unmounted: invalidate this run so its response is dropped.
    return invalidateRequests;
    // viewerGen: a viewer change must refetch even though the org did not change.
  }, [organizationId, fetchProfiles, invalidateRequests, viewerGen]);

  const getProfileById = useCallback((id: string) => profiles.find((p) => p.id === id), [profiles]);

  const getProfileByGhlId = useCallback(
    (ghlId: string) => profiles.find((p) => p.ghl_user_id === ghlId),
    [profiles]
  );

  return { allProfiles: profiles, isLoading, error, getProfileById, getProfileByGhlId };
}
